import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useGoogleLogin } from "@react-oauth/google";
import { Flourish, Fleuron } from "@/components/Ornaments";
import { api } from "@/lib/api";

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#EA4335" d="M9 3.6c1.32 0 2.5.46 3.43 1.35l2.54-2.54C13.46 1.09 11.43.2 9 .2 5.48.2 2.44 2.22.96 5.18l2.96 2.3C4.65 5.27 6.65 3.6 9 3.6z"/>
      <path fill="#4285F4" d="M17.64 9.2c0-.63-.06-1.25-.16-1.84H9v3.48h4.84c-.21 1.12-.85 2.07-1.81 2.71l2.93 2.27c1.71-1.58 2.68-3.91 2.68-6.62z"/>
      <path fill="#FBBC05" d="M3.92 10.71a5.4 5.4 0 0 1 0-3.42L.96 5C.35 6.23 0 7.58 0 9c0 1.42.35 2.77.96 4l2.96-2.29z"/>
      <path fill="#34A853" d="M9 17.8c2.43 0 4.47-.81 5.95-2.18l-2.93-2.27c-.82.55-1.86.88-3.02.88-2.35 0-4.34-1.66-5.05-3.89l-2.96 2.3C2.44 15.78 5.48 17.8 9 17.8z"/>
    </svg>
  );
}

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // @react-oauth/google's `flow: 'auth-code'` opens a Google popup which posts
  // the one-time code back to this callback. Backend then exchanges it server-
  // side (where the client secret lives) for tokens + userinfo.
  const signInGoogle = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async ({ code }) => {
      try {
        const res = await api.post("/auth/google/exchange", {
          code,
          redirect_uri: "postmessage",
        });
        const token = res.data?.session_token;
        if (token) {
          try { localStorage.setItem("lc_session_token", token); } catch { /* private mode */ }
        }
        setUser(res.data);
        navigate("/", { replace: true });
      } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || "Sign-in failed";
        toast.error(typeof detail === "string" ? detail : "Sign-in failed");
      }
    },
    onError: () => toast.error("Google sign-in cancelled"),
  });

  return (
    <div className="paper-grain min-h-screen relative flex items-center justify-center px-6 py-10">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9 }}
        className="relative z-10 max-w-md w-full text-center"
      >
        <Fleuron size={32} color="var(--bronze)" className="mx-auto opacity-80" />
        <h1 className="mt-6 font-serif-lit text-5xl sm:text-6xl tracking-tight text-[var(--ink)] leading-[1.02]">
          Curated <span className="italic font-garamond text-[var(--bronze)]">Ethos</span>
        </h1>
        <Flourish color="var(--bronze)" className="mx-auto mt-5 opacity-80" />

        <p className="mt-7 font-garamond italic text-lg text-[var(--ink-dusty)] leading-relaxed">
          Welcome to a community that runs on taste, not algorithms.
        </p>

        <div className="mt-10 flex flex-col items-stretch" data-testid="auth-providers">
          <ProviderButton
            label="Continue with Google"
            icon={<GoogleMark />}
            onClick={() => signInGoogle()}
            testId="signin-google"
          />
        </div>

        <p className="mt-10 font-cinzel text-[10px] tracking-[0.32em] uppercase text-[var(--ink-light)]">
          Taste, not algorithms
        </p>

        <p className="mt-6 font-garamond italic text-[13px] text-[var(--ink-light)]">
          Your shelf stays yours. Share what you want, when you want.
        </p>
      </motion.div>
    </div>
  );
}

function ProviderButton({ label, icon, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="group relative flex items-center justify-center gap-3 w-full py-3.5 px-5 rounded-full
                 bg-[var(--card-bg)] border border-[rgba(140,106,47,0.32)]
                 hover:bg-[var(--paper-aged)] hover:border-[var(--bronze)]
                 transition-all duration-300 shadow-[0_2px_8px_rgba(42,30,20,0.06)]"
    >
      <span className="flex items-center justify-center">{icon}</span>
      <span className="font-cinzel text-[12px] tracking-[0.18em] uppercase text-[var(--ink)]">
        {label}
      </span>
    </button>
  );
}
